import { Alert, Form, GetProp, Input, Modal, Row, Upload, UploadFile, UploadProps } from "antd"
import { useEffect, useState } from "react";
import { Company } from "src/common/models/company";
import { getInputRequiredRule } from "src/common/util";
import { Col6 } from "src/components/Columns";
import { FormActionButtons } from "src/components/FormActionButtons";
import * as Styles from './styles'
import { PlusOutlined } from '@ant-design/icons';
import { CommonError } from "src/common/state/types";
import { useSelector } from "react-redux";
import { RootState } from "src/state/reducer";
import { SystemDescriptions } from "src/common/descriptions";

interface ReduxProps {
    error?: CommonError
}

interface Props {
    initialValues?: Company
    onFinish: (service: Company) => void
    onCancel: VoidFunction
}

export const CompanyForm = (props: Props) => {
    const [form] = Form.useForm();

    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [hasImageChanged, setHasImageChanged] = useState<boolean>(false)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.common.error,
    }))

    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    useEffect(() => {
        if (props.initialValues) {
            form.setFieldsValue({
                name: props.initialValues.name,
                email: props.initialValues.email,
                address: props.initialValues.address,
                phone: props.initialValues.phone,
                city: props.initialValues.city,
                state: props.initialValues.state,
                slogan: props.initialValues.slogan,
            })

            if (props.initialValues.logoUrl) {
                setPreviewImage(props.initialValues?.logoUrl);

                setFileList([
                    {
                        uid: props.initialValues.id!.toString(),
                        name: `${props.initialValues.name}.jpg`,
                        status: "done",
                        url: props.initialValues.logoUrl,
                    },
                ])
            }
        }
    }, [])

    const getImageBase64 = async (): Promise<string | undefined> => {
        if (hasImageChanged) {
            return fileList.length > 0
                ? await getBase64(fileList[0].originFileObj as any)
                : ''
        }

        return undefined
    }

    const onFinish = async (formValues) => {
        const company: Company = {
            id: props.initialValues?.id!,
            address: formValues.address,
            city: formValues.city,
            state: formValues.state,
            email: formValues.email,
            name: formValues.name,
            phone: formValues.phone,
            slogan: formValues.slogan,
            logoUrl: (hasImageChanged && props.initialValues?.logoUrl) ? '' : props.initialValues?.logoUrl!,
            imageBase64: await getImageBase64(),
        }

        props.onFinish(company)
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={"Cancel"}
            actionText={"Save"}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
        />
    )

    type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

    const getBase64 = (file: FileType): Promise<string> => (
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file as any);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        })
    )

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const beforeUpload = (file) => {
        const allowedImageTypes: string[] = [
            "image/jpeg",
            "image/jpg",
        ]

        const isAllowed = allowedImageTypes.includes(file.type);
        if (!isAllowed) {
            alert('El formato de la imágen no es permitido');
        }

        setHasImageChanged(true)

        return isAllowed || Upload.LIST_IGNORE;
    };

    const renderFileSelector = () => {
        return <Col6>
            <Form.Item
                label={"Image"}
            >
                <Styles.ImageSelectorContainer>
                    <Upload
                        listType="picture-card"
                        fileList={fileList}
                        onPreview={handlePreview}
                        onChange={handleChange}
                        beforeUpload={beforeUpload}
                        showUploadList={{ showRemoveIcon: true }}
                        customRequest={({ file, onSuccess }) => {
                            setTimeout(() => {
                                if (onSuccess)
                                    onSuccess("ok");
                            }, 0);
                        }}
                    >
                        {
                            (fileList.length >= 1)
                                ? null
                                : <div><PlusOutlined /><div style={{ marginTop: 8 }}>
                                    {"Upload"}
                                </div></div>}
                    </Upload>
                    <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)}>
                        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
                    </Modal>
                </Styles.ImageSelectorContainer>
            </Form.Item>
        </Col6>
    }

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col6>
                    <Form.Item
                        label={"Name"}
                        name={"name"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={"Email"}
                        name={"email"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={"Address"}
                        name={"address"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={"City"}
                        name={"city"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={"State"}
                        name={"state"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={"Slogan"}
                        name={"slogan"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={"Phone"}
                        name={"phone"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={"Name"}
                        />
                    </Form.Item>
                </Col6>
                {renderFileSelector()}
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"Error"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
